import { AxiosResponse } from 'axios';
import {
  IGetInvoicesQuery,
  IGetInvoicesResponse,
  IRevokeManualPaymentResponse,
  ICancelInvoiceResponse,
  ICancelRecurringInvoiceResponse,
  IDeleteInvoiceResponse,
  IProcessRefundRequest,
  IProcessRefundResponse,
} from '../../interface';
import {
  IInvoiceForEditResponse,
  IInvoiceListQuery,
  IInvoiceListResponse,
  INextBillingPreviewApiResponse,
  INextBillingPreviewRequest,
  IUpdateInvoiceRequest,
  IUpdateInvoiceResponse,
} from '../../interface/studentBillingModel';
import api from '../api';

const getInvoices = async (
  query: IGetInvoicesQuery,
): Promise<AxiosResponse<IGetInvoicesResponse>> => {
  const res: AxiosResponse<IGetInvoicesResponse> = await api.get('/payment/invoices', {
    params: query,
  });
  return res;
};

interface ManualPaymentData {
  amount_received: number;
  payment_date?: string;
  reference_note?: string;
}

const manualPayment = async (
  assign_invoice_id: string,
  data: ManualPaymentData,
): Promise<AxiosResponse<any>> => {
  const res: AxiosResponse<any> = await api.put(`/payment/manual/${assign_invoice_id}`, data);
  return res;
};

const revokeManualPayment = async (
  assign_invoice_id: string,
  revocation_note: string,
): Promise<AxiosResponse<IRevokeManualPaymentResponse>> => {
  const res: AxiosResponse<IRevokeManualPaymentResponse> = await api.put(
    `/payment/revoke-manual/${assign_invoice_id}`,
    { revocation_note },
  );
  return res;
};

const cancelInvoice = async (
  assign_invoice_id: string,
  cancellation_reason: string,
): Promise<AxiosResponse<ICancelInvoiceResponse>> => {
  const res: AxiosResponse<ICancelInvoiceResponse> = await api.put(
    `/payment/cancel-invoice/${assign_invoice_id}`,
    { cancellation_reason },
  );
  return res;
};

const cancelRecurringInvoice = async (
  invoiceId: string,
  cancellation_reason: string,
): Promise<AxiosResponse<ICancelRecurringInvoiceResponse>> => {
  const res: AxiosResponse<ICancelRecurringInvoiceResponse> = await api.put(
    `/payment/cancel-recurring-invoice/${invoiceId}`,
    { cancellation_reason },
  );
  return res;
};

const deleteInvoice = async (
  assign_invoice_id: string,
  delete_reason: string,
): Promise<AxiosResponse<IDeleteInvoiceResponse>> => {
  const res: AxiosResponse<IDeleteInvoiceResponse> = await api.delete(
    `/payment/assign-invoices/${assign_invoice_id}`,
    { data: { delete_reason } },
  );
  return res;
};

const processRefund = async (
  assign_invoice_id: string,
  data: IProcessRefundRequest,
): Promise<AxiosResponse<IProcessRefundResponse>> => {
  const res: AxiosResponse<IProcessRefundResponse> = await api.put(
    `/payment/refund/${assign_invoice_id}`,
    data,
  );
  return res;
};

const getInvoiceList = async (
  query: IInvoiceListQuery,
): Promise<AxiosResponse<IInvoiceListResponse>> => {
  const res: AxiosResponse<IInvoiceListResponse> = await api.get('/payment/invoices', {
    params: query,
  });
  return res;
};

const updateInvoice = async (
  invoiceId: string,
  data: IUpdateInvoiceRequest,
): Promise<AxiosResponse<IUpdateInvoiceResponse>> => {
  const res: AxiosResponse<IUpdateInvoiceResponse> = await api.put(
    `/payment/invoice/${invoiceId}`,
    data,
  );
  return res;
};

const getInvoiceById = async (
  invoiceId: string,
): Promise<AxiosResponse<IInvoiceForEditResponse>> => {
  const res: AxiosResponse<IInvoiceForEditResponse> = await api.get(
    `/payment/invoices/${invoiceId}`,
  );
  return res;
};

const previewNextBilling = async (
  invoiceId: string,
  data: INextBillingPreviewRequest,
): Promise<AxiosResponse<INextBillingPreviewApiResponse>> => {
  const res: AxiosResponse<INextBillingPreviewApiResponse> = await api.post(
    `/payment/invoice/${invoiceId}/next-billing-preview`,
    data,
  );
  return res;
};

export const paymentService = {
  previewNextBilling,
  getInvoices,
  getInvoiceList,
  getInvoiceById,
  updateInvoice,
  manualPayment,
  revokeManualPayment,
  cancelInvoice,
  cancelRecurringInvoice,
  deleteInvoice,
  processRefund,
};
